/**
 * OrgUnitEmployee Migration Utility
 * Creates OrgUnitEmployee assignments from existing OrgUnit managers
 * Part of Phase 5: Employee Type Implementation
 */

import { API, graphqlOperation } from 'aws-amplify';
import { v4 as uuidv4 } from 'uuid';
import { listOrgUnits, listAreas2, listUsers, getUser, roleDefinitionsByClient } from 'graphql/queries';
import { createOrgUnitEmployee } from 'graphql/mutations';
import { getOrCreateEmployeeForUser } from './employeeMigration';

/**
 * Map OrgLevel to appropriate role code
 */
const ORG_LEVEL_TO_ROLE_CODE = {
  'ORGLEVEL_1': 'AM',  // Area Manager
  'ORGLEVEL_2': 'DM',  // District Manager
  'ORGLEVEL_3': 'RM'   // Regional Manager
};

/**
 * Migrate OrgUnit managers to OrgUnitEmployee assignments
 * Uses listAreas2 data to get area managers from the areaManager field
 * @param {string} clientId - Client identifier
 * @param {Map} employeeMap - Map of userId to employeeId
 * @param {Function} onProgress - Progress callback
 * @returns {Object} Migration results
 */
export async function migrateOrgUnitManagers(clientId, employeeMap, onProgress = () => {}) {
  const results = {
    created: 0,
    skipped: 0,
    errors: [],
    assignments: []
  };

  try {
    onProgress('Fetching Areas with managers from listAreas2...');
    
    // Fetch all Areas from listAreas2 (legacy data)
    // The report parameter is required - using 'migration' as a placeholder
    const areasData = await API.graphql(
      graphqlOperation(listAreas2, {
        report: clientId  // Using clientId as the report parameter since it's required
      })
    );
    
    const areas = areasData.data.listAreas2 || [];
    onProgress(`Found ${areas.length} areas from legacy data`);

    // Fetch all OrgUnits for mapping
    const orgUnitData = await API.graphql(
      graphqlOperation(listOrgUnits, {
        filter: { clientId: { eq: clientId } }
      })
    );
    
    const orgUnits = orgUnitData.data.listOrgUnits.items;
    
    // Create a map of area names to OrgUnit IDs for matching
    const areaNameToOrgUnit = new Map();
    orgUnits.forEach(orgUnit => {
      // Match by name - OrgUnit name should match area name
      areaNameToOrgUnit.set(orgUnit.name, orgUnit);
    });

    // Fetch all users to map usernames to user records
    // IMPORTANT: Must paginate to get ALL users, not just first page
    onProgress('Loading all users for username lookup...');
    let allUsers = [];
    let nextToken = null;
    
    do {
      const usersData = await API.graphql(
        graphqlOperation(listUsers, {
          filter: { client: { eq: clientId } },
          limit: 100,
          nextToken
        })
      );
      
      const items = usersData.data.listUsers?.items || [];
      allUsers = allUsers.concat(items);
      nextToken = usersData.data.listUsers?.nextToken;
    } while (nextToken);
    
    const usernameToUser = new Map();
    allUsers.forEach(user => {
      usernameToUser.set(user.userName, user);
    });
    
    onProgress(`Loaded ${allUsers.length} users for lookup`);

    // Fetch role definitions
    const roleData = await API.graphql(
      graphqlOperation(roleDefinitionsByClient, {
        clientId
      })
    );
    
    const roleMap = new Map();
    roleData.data.roleDefinitionsByClient.items.forEach(role => {
      roleMap.set(role.roleCode, role);
    });

    // Get default Area Manager role (AM)
    const areaManagerRole = roleMap.get('AM');
    if (!areaManagerRole) {
      console.error('Area Manager (AM) role definition not found');
      results.errors.push({
        error: 'Area Manager (AM) role definition not found - please create roles first'
      });
      return results;
    }

    // Process each area
    for (const area of areas) {
      try {
        // Skip if no area manager
        if (!area.areaManager) {
          console.log(`Area ${area.areaName} has no manager`);
          results.skipped++;
          continue;
        }

        // Find the corresponding OrgUnit
        const orgUnit = areaNameToOrgUnit.get(area.areaName);
        if (!orgUnit) {
          console.warn(`OrgUnit not found for area ${area.areaName}`);
          results.errors.push({
            areaName: area.areaName,
            error: 'Corresponding OrgUnit not found'
          });
          continue;
        }

        // Find the user by username
        const user = usernameToUser.get(area.areaManager);
        if (!user) {
          console.warn(`User not found for username ${area.areaManager}`);
          results.errors.push({
            areaName: area.areaName,
            managerUsername: area.areaManager,
            error: 'User record not found'
          });
          continue;
        }

        // Get or create Employee for this User
        let employeeId = employeeMap.get(user.id);
        if (!employeeId) {
          employeeId = await getOrCreateEmployeeForUser(user, clientId);
          employeeMap.set(user.id, employeeId);
        }

        // Create OrgUnitEmployee assignment
        const assignmentInput = {
          clientId,
          employeeId,
          orgUnitId: orgUnit.id,
          managementLevel: orgUnit.orgLevel || 'ORGLEVEL_1',
          roleDefinitionId: areaManagerRole.id,
          roleSnapshot: areaManagerRole.roleName,
          isActive: true,
          effectiveDate: orgUnit.lastModified || new Date().toISOString(),
          assignedBy: 'SYSTEM',
          assignmentReason: 'Migrated from listAreas2.areaManager',
          employeeNameSnapshot: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.userName,
          orgUnitNameSnapshot: orgUnit.name,
          createdAt: new Date().toISOString(),
          lastModified: new Date().toISOString()
        };

        const assignmentResult = await API.graphql(
          graphqlOperation(createOrgUnitEmployee, { input: assignmentInput })
        );
        
        results.created++;
        results.assignments.push({
          orgUnitId: orgUnit.id,
          orgUnitName: orgUnit.name,
          employeeId,
          username: area.areaManager,
          assignmentId: assignmentResult.data.createOrgUnitEmployee.id
        });

        onProgress(`Created assignment for ${area.areaManager} at ${area.areaName}`);
        
      } catch (error) {
        console.error(`Error migrating area ${area.areaName}:`, error);
        results.errors.push({
          areaName: area.areaName,
          areaManager: area.areaManager,
          error: error.message
        });
      }
    }

    onProgress(`Migration complete: ${results.created} OrgUnit assignments created`);
    return results;
    
  } catch (error) {
    console.error('Fatal error in OrgUnit manager migration:', error);
    throw error;
  }
}

/**
 * Create OrgUnitEmployee assignment for a specific OrgUnit and employee
 * @param {Object} params - Assignment parameters
 * @returns {Object} Created assignment
 */
export async function createOrgUnitAssignment({
  clientId,
  employeeId,
  orgUnitId,
  managementLevel,
  roleDefinitionId,
  roleName,
  effectiveDate = new Date().toISOString(),
  assignedBy,
  assignmentReason,
  employeeName,
  orgUnitName
}) {
  const input = {
    clientId,
    employeeId,
    orgUnitId,
    managementLevel,
    roleDefinitionId,
    roleSnapshot: roleName,
    isActive: true,
    effectiveDate,
    assignedBy: assignedBy || 'SYSTEM',
    assignmentReason: assignmentReason || 'Manual assignment',
    employeeNameSnapshot: employeeName,
    orgUnitNameSnapshot: orgUnitName,
    createdAt: new Date().toISOString(),
    lastModified: new Date().toISOString()
  };

  const result = await API.graphql(
    graphqlOperation(createOrgUnitEmployee, { input })
  );

  return result.data.createOrgUnitEmployee;
}